// Route: /legal/$kind — read-only просмотр юридического документа.
//
// ─────────────────────────────────────────────────────────────────────────────
// Два источника текста, и путать их нельзя
// ─────────────────────────────────────────────────────────────────────────────
// * НАШИ документы (`ai-disclaimer`, `partner-data-sharing`) — про наш продукт,
//   живут в i18n и переводятся как обычный контент.
// * Документы ЭКОСИСТЕМЫ (`privacy`, `terms`, `cookie`) — одна редакция на все
//   сервисы, живут в Legal Center IEC ID и берутся оттуда по сети
//   (`iecId.legal.document`). Держать их копию у себя запрещено
//   (docs/INTEGRATION-GUIDE.md §3.5): копия разъедется с действующей редакцией,
//   и принятым окажется не тот текст.
//
// Текст экосистемы приходит ТОЛЬКО на английском и НЕ переводится: перевод
// юридического документа — это его новая редакция, а не то же самое другими
// словами (§3). Локализуется всё ВОКРУГ: заголовки, подписи, кнопки, пометки.
//
// Тактильный экран «Картотека»: спокойный заголовок, документ на панели e2,
// читаемо без давления. Обе темы.

import { Button, cn, ElevatedPanel, NARROW_PAGE_SHELL, PANEL_PAD, Skeleton } from "@studyhub/ui";
import {
	IconAlertCircle,
	IconChevronRight,
	IconExternalLink,
	IconFileText,
	IconWorld,
} from "@tabler/icons-react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import ReactMarkdown from "react-markdown";
import { LEGAL_PROSE_CLASS } from "@/components/legal/legal-prose";
import { PageMeta } from "@/components/seo/PageMeta";
import { PagePendingFallback } from "@/components/shared/page-pending-fallback";
import { openIecIdSection } from "@/lib/iec-id-portal";
import { api } from "@/lib/trpc-client";

/**
 * Наш документ: текст берётся из i18n по ключу `documents.<i18nKey>`.
 * Документ экосистемы: текст берётся из Legal Center по ключу `iecKey`, а
 * `labelKey` — только подпись в списке и в шапке (`documentsIndex.ecosystem.*`).
 */
type LegalDocEntry =
	| { kind: string; source: "local"; i18nKey: string }
	| { kind: string; source: "ecosystem"; iecKey: string; labelKey: string };

const DOC_KINDS: LegalDocEntry[] = [
	// Ключи Legal Center — из §3.1 гайда, менять нельзя: по ним ходит их API.
	{ kind: "terms", source: "ecosystem", iecKey: "terms-of-use", labelKey: "terms" },
	{ kind: "privacy", source: "ecosystem", iecKey: "privacy-policy", labelKey: "privacy" },
	{ kind: "cookie", source: "ecosystem", iecKey: "cookies", labelKey: "cookie" },
	{ kind: "ai-disclaimer", source: "local", i18nKey: "aiDisclaimer" },
	// Документ, под который подписываются в гейте модуля «Персонажи»
	// (`legal_documents.type = 'partner_data_sharing'`). Кнопка «Читать документ»
	// ведёт СЮДА — раньше она вела на privacy, то есть на чужой текст.
	{ kind: "partner-data-sharing", source: "local", i18nKey: "partnerDataSharing" },
];

/** kind → описание документа. Единственный источник соответствия. */
const ENTRY_BY_KIND = new Map<string, LegalDocEntry>(DOC_KINDS.map((d) => [d.kind, d]));

export const Route = createFileRoute("/legal/$kind")({
	component: LegalDocumentPage,
	// kind остаётся строкой: невалидные значения (в т.ч. `/legal/documents`)
	// рендерят индекс юр-документов, а не падают.
	parseParams: (params) => ({
		kind: params.kind,
	}),
	pendingComponent: PagePendingFallback,
});

function LegalDocumentPage(): React.ReactElement {
	const { kind } = Route.useParams();

	// Невалидный kind (в т.ч. `/legal/documents`) → индекс всех юр-документов.
	const entry = ENTRY_BY_KIND.get(kind);
	if (!entry) return <LegalDocumentsIndex />;
	if (entry.source === "ecosystem") return <EcosystemDocumentPage entry={entry} />;
	return <LocalDocumentPage kind={entry.kind} i18nKey={entry.i18nKey} />;
}

/** Наш документ — текст из i18n, как было. */
function LocalDocumentPage({
	kind,
	i18nKey,
}: {
	kind: string;
	i18nKey: string;
}): React.ReactElement {
	const { t } = useTranslation("legal");
	const title = t(`documents.${i18nKey}.title`);
	const version = t(`documents.${i18nKey}.version`);
	const body = t(`documents.${i18nKey}.body`);

	return (
		<DocumentShell path={`/legal/${kind}`} title={title} subtitle={version}>
			<ElevatedPanel className={cn(PANEL_PAD, "mt-6 overflow-hidden")}>
				<article className={LEGAL_PROSE_CLASS}>
					<ReactMarkdown>{body}</ReactMarkdown>
				</article>
			</ElevatedPanel>
		</DocumentShell>
	);
}

/** Документ экосистемы — текст из Legal Center, обвязка локализована. */
function EcosystemDocumentPage({
	entry,
}: {
	entry: Extract<LegalDocEntry, { source: "ecosystem" }>;
}): React.ReactElement {
	const { t } = useTranslation("legal");
	const doc = api.iecId.legal.document.useQuery({ key: entry.iecKey });
	const fallbackTitle = t(`documentsIndex.ecosystem.${entry.labelKey}`);

	return (
		<DocumentShell
			path={`/legal/${entry.kind}`}
			title={doc.data?.title || fallbackTitle}
			subtitle={
				doc.data ? t("ecosystem.version", { version: doc.data.version }) : t("ecosystem.subtitle")
			}
		>
			{/* Пометка обязательна: человек должен понимать, что принял этот текст
			    один раз на всю экосистему, а управляет им в едином профиле, а не
			    здесь. Без неё страница выглядит как «документ StudyHub». */}
			<ElevatedPanel className={cn(PANEL_PAD, "mt-6 flex items-start gap-3")}>
				<IconWorld size={18} className="mt-0.5 shrink-0 text-primary" stroke={1.75} />
				<div className="min-w-0 flex-1">
					<p className="text-sm leading-relaxed text-foreground">{t("ecosystem.notice")}</p>
					<p className="mt-1 text-xs leading-relaxed text-muted-foreground">
						{t("ecosystem.englishOnly")}
					</p>
					<Button
						variant="outline"
						size="sm"
						className="mt-3 cursor-pointer"
						onClick={() => openIecIdSection("legal")}
					>
						{t("ecosystem.openInProfile")}
						<IconExternalLink size={16} stroke={1.75} />
					</Button>
				</div>
			</ElevatedPanel>

			<ElevatedPanel className={cn(PANEL_PAD, "mt-4 overflow-hidden")}>
				{doc.isPending ? (
					<div className="flex flex-col gap-3">
						<Skeleton className="h-5 w-2/3" />
						<Skeleton className="h-4 w-full" />
						<Skeleton className="h-4 w-full" />
						<Skeleton className="h-4 w-5/6" />
						<Skeleton className="h-4 w-3/4" />
					</div>
				) : doc.data ? (
					// `lang="en"` не косметика: скринридер обязан прочитать английский
					// текст английским голосом, а браузер — не предлагать перевод как
					// равнозначный (перевод — другая редакция).
					<article className={LEGAL_PROSE_CLASS} lang={doc.data.resolvedLocale || "en"}>
						<ReactMarkdown>{doc.data.bodyMarkdown}</ReactMarkdown>
					</article>
				) : (
					// Честный отказ вместо устаревшей копии: показать свой старый текст
					// как действующий — ровно то, что §3.5 запрещает.
					<div className="flex items-start gap-3">
						<IconAlertCircle size={18} className="mt-0.5 shrink-0 text-destructive" stroke={1.75} />
						<div className="min-w-0 flex-1">
							<p className="text-sm font-medium text-foreground">{t("ecosystem.loadErrorTitle")}</p>
							<p className="mt-1 text-sm text-muted-foreground">{t("ecosystem.loadErrorBody")}</p>
							<div className="mt-3 flex flex-wrap gap-2">
								<Button
									variant="outline"
									size="sm"
									className="cursor-pointer"
									onClick={() => void doc.refetch()}
								>
									{t("ecosystem.retry")}
								</Button>
								<Button
									variant="ghost"
									size="sm"
									className="cursor-pointer"
									onClick={() => openIecIdSection("legal")}
								>
									{t("ecosystem.openInProfile")}
									<IconExternalLink size={16} stroke={1.75} />
								</Button>
							</div>
						</div>
					</div>
				)}
			</ElevatedPanel>
		</DocumentShell>
	);
}

/** Общая рамка страницы документа — заголовок + контент. */
function DocumentShell({
	path,
	title,
	subtitle,
	children,
}: {
	path: string;
	title: string;
	subtitle: string;
	children: React.ReactNode;
}): React.ReactElement {
	return (
		<div className="flex min-h-dvh flex-col bg-background pt-[env(safe-area-inset-top)]">
			<PageMeta title={title} description={subtitle} path={path} />

			<main className={cn(NARROW_PAGE_SHELL, "flex-1")}>
				<div className="flex items-start gap-3">
					<div className="flex size-10 shrink-0 items-center justify-center rounded-[var(--radius-tile)] bg-primary/10 [box-shadow:inset_0_1px_0_rgb(255_255_255/0.4)]">
						<IconFileText size={20} className="text-primary" stroke={1.75} />
					</div>
					<div className="min-w-0">
						<h1 className="text-2xl font-semibold tracking-tight lg:text-3xl">{title}</h1>
						<p className="mt-1 text-sm tabular-nums text-muted-foreground">{subtitle}</p>
					</div>
				</div>

				{children}
			</main>
		</div>
	);
}

/** Индекс юридических документов — наши и документы экосистемы одним списком. */
function LegalDocumentsIndex(): React.ReactElement {
	const { t } = useTranslation("legal");
	return (
		<div className="flex min-h-dvh flex-col bg-background pt-[env(safe-area-inset-top)]">
			<PageMeta title={t("documentsIndex.title")} path="/legal/documents" />
			<main className={cn(NARROW_PAGE_SHELL, "flex-1")}>
				<div className="flex items-start gap-3">
					<div className="flex size-10 shrink-0 items-center justify-center rounded-[var(--radius-tile)] bg-primary/10 [box-shadow:inset_0_1px_0_rgb(255_255_255/0.4)]">
						<IconFileText size={20} className="text-primary" stroke={1.75} />
					</div>
					<div className="min-w-0">
						<h1 className="text-2xl font-semibold tracking-tight lg:text-3xl">
							{t("documentsIndex.title")}
						</h1>
						<p className="mt-1 text-sm text-muted-foreground">{t("documentsIndex.subtitle")}</p>
					</div>
				</div>

				<ElevatedPanel className="mt-6 divide-y divide-border overflow-hidden p-0">
					{DOC_KINDS.map((d) => (
						<Link
							key={d.kind}
							to="/legal/$kind"
							params={{ kind: d.kind }}
							className="flex items-center gap-3 px-5 py-4 transition-colors duration-fast hover:bg-muted/40"
						>
							{d.source === "ecosystem" ? (
								<IconWorld size={18} className="shrink-0 text-muted-foreground" stroke={1.75} />
							) : (
								<IconFileText size={18} className="shrink-0 text-muted-foreground" stroke={1.75} />
							)}
							<div className="min-w-0 flex-1">
								<div className="text-sm font-medium text-foreground">
									{d.source === "ecosystem"
										? t(`documentsIndex.ecosystem.${d.labelKey}`)
										: t(`documents.${d.i18nKey}.title`)}
								</div>
								<div className="text-2xs text-muted-foreground">
									{d.source === "ecosystem"
										? t("ecosystem.badge")
										: t(`documents.${d.i18nKey}.version`)}
								</div>
							</div>
							<IconChevronRight
								size={16}
								className="shrink-0 text-muted-foreground"
								stroke={1.75}
							/>
						</Link>
					))}
				</ElevatedPanel>
			</main>
		</div>
	);
}
